import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Popover, Tag } from 'antd';
import { ArrowCounterclockwise, ThreeDotsVertical, Pencil, Trash } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import TableComponent from '../common/TableComponent';
import { ActionMenu } from '../common/ActionMenu';
import type { ProductCategory } from '../../types/productCategory';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';

interface MainProduct {
  id: number;
  name: string;
  productCategoryId?: number;
  productCategory?: {
    id: number;
  };
  isActive?: boolean;
}

interface ProductCategoryTableProps {
  categories: ProductCategory[];
  loading?: boolean;
  totalCategories: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number, size?: number) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
}

type ProductCategoryMenuItem = {
  text: string;
  icon: React.ReactNode;
  click: () => void;
  disabled?: boolean;
  description?: string;
};

const ProductCategoryTable: React.FC<ProductCategoryTableProps> = ({
  categories,
  loading = false,
  totalCategories,
  currentPage,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  onRestore,
}) => {
  const { t } = useTranslation(['productCategory', 'common']);
  const { hasPermission } = usePermissions();
  const { isOnline } = useIsOnlineContext();
  const [mainProductsByCategory, setMainProductsByCategory] = useState<
    Record<number, MainProduct[]>
  >({});
  const canManageProductCategories = hasPermission([
    PermissionEnum.MANAGE_PRODUCT_CATEGORIES,
    PermissionEnum.PRODUCT_CATEGORIES,
  ]);

  useEffect(() => {
    const loadMainProductsForCategories = async () => {
      const mainProductsMap: Record<number, MainProduct[]> = {};

      try {
        const mainProducts = await referenceDataSyncService.getMainProducts();

        for (const category of categories) {
          mainProductsMap[category.id] = mainProducts.filter((mainProduct) => {
            const categoryId = mainProduct.productCategoryId ?? mainProduct.productCategory?.id;
            return categoryId === category.id && mainProduct.isActive !== false;
          });
        }
      } catch (error) {
        console.error('Error loading main products for product categories:', error);

        for (const category of categories) {
          mainProductsMap[category.id] = [];
        }
      }

      setMainProductsByCategory(mainProductsMap);
    };

    if (categories.length > 0) {
      loadMainProductsForCategories();
    } else {
      setMainProductsByCategory({});
    }
  }, [categories]);

  const getCategoryColumns = () => [
    {
      title: t('mainCategory'),
      dataIndex: 'mainCategory',
      key: 'mainCategory',
      width: 150,
    },
    {
      title: t('subCategory'),
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category: string | null) => <span className="text-gray-600">{category || '-'}</span>,
    },

    {
      title: t('productCount'),
      dataIndex: 'productCount',
      key: 'productCount',
      width: 120,
      align: 'center' as const,
      render: (_: number, record: ProductCategory) => {
        const mainProducts = mainProductsByCategory[record.id] || [];
        const productCount = mainProducts.length || record.productCount || 0;

        if (productCount === 0) {
          return <span className="text-gray-600">N/A</span>;
        }

        const mainProductNames = mainProducts.map((mainProduct) => mainProduct.name);

        return (
          <Popover
            content={
              <div className="max-h-[150px] max-w-xs space-y-1 overflow-auto">
                {mainProductNames.length > 0 ? (
                  mainProductNames.map((name, index) => (
                    <div key={index} className="text-sm text-gray-700">
                      {'\u2022'} {name}
                    </div>
                  ))
                ) : (
                  <span className="text-sm text-gray-600">N/A</span>
                )}
              </div>
            }
            title={t('mainProducts')}
            trigger="click"
            placement="top"
          >
            <Tag className="cursor-pointer transition-colors hover:bg-blue-100" color="blue">
              {productCount}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? t('common:statusFilter.active') : t('common:statusFilter.inactive')}</Tag>
      ),
    },
    {
      title: t('created'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 120,
      align: 'center' as const,
      render: (createTime: number) => (
        <span className="text-sm text-gray-600">{dayjs(createTime).format('YYYY-MM-DD')}</span>
      ),
    },
    ...(canManageProductCategories
      ? [
        {
          title: t('action'),
          dataIndex: 'action',
          key: 'action',
          width: 100,
          align: 'center' as const,
          render: (_: unknown, record: ProductCategory) => {
            const menuItems: ProductCategoryMenuItem[] = [];

            if (record.isActive === false) {
              menuItems.push({
                text: t('restore'),
                icon: <ArrowCounterclockwise size={16} className="text-primary-blue-icon" />,
                click: () => onRestore(record.id.toString()),
                disabled: !isOnline,
                description: !isOnline ? t('offlineRestoreInfoTitle') : undefined,
              });
            } else {
              menuItems.push(
                  {
                    text: t('edit'),
                    icon: <Pencil size={16} className="text-primary-blue-icon" />,
                    click: () => onEdit(record.id.toString()),
                    disabled: !isOnline,
                    description: !isOnline ? t('offlineEditInfoTitle') : undefined,
                  },
                  {
                    text: t('delete'),
                    icon: <Trash size={16} className="text-primary-blue-icon" />,
                    click: () => onDelete(record.id.toString()),
                    disabled: !isOnline,
                    description: !isOnline ? t('offlineDeleteInfoTitle') : undefined,
                  }
              );
            }

            return (
              <Popover
                content={<ActionMenu menuItems={menuItems} />}
                className="cursor-pointer"
                placement="bottom"
                trigger="hover"
                overlayStyle={{ zIndex: 800 }}
              >
                <Row justify="center">
                  <ThreeDotsVertical size={15} />
                </Row>
              </Popover>
            );
          },
        },
      ]
      : []),
  ];

  return (
    // <BasicCard>
    // <div className="p-4">
    <div className="mt-6 w-full overflow-x-auto">
      <TableComponent
        tableData={categories}
        pageSize={pageSize}
        currentPage={currentPage}
        totalItems={totalCategories}
        onPaginationChanged={onPageChange}
        columns={getCategoryColumns()}
        loading={loading}
        showHeader
      />
    </div>
    // </div>
    // </BasicCard>
  );
};

export default ProductCategoryTable;
